import { multiTinLookup } from "../checkoutConstants";
import { FREE_MATCHA_TIN_SKU, isMatchaLineItem } from "../tinLineItems";
import { promoOptionsData } from "./PromoCodeOptions";

// Money in the shape CartLineItem reads. These items are never charged for, so
// every amount is zero and the component renders "FREE" off the back of it.
const NO_CHARGE = { amount: "0.00", currency: null, formatted: "Free" };

const freeDisplayLineItem = ({ id, imageUrl, title, type }) => ({
  id,
  type,
  quantity: 1,
  product: { title, image: imageUrl ? { url: imageUrl } : null },
  discount: NO_CHARGE,
  subtotal: NO_CHARGE,
  total: NO_CHARGE,
});

// The tin selector names tins from the same lookup, so "Pods" shows as
// "Pink Grind Tin" here too rather than as the raw variant option.
const tinTitle = (tin) => {
  const targetTin = Object.values(multiTinLookup).find(({ variantTitles }) =>
    variantTitles.includes(tin.variant?.title),
  );

  if (targetTin) {
    return targetTin.title;
  }

  if (tin.variant?.title && tin.variant.title !== "Default Title") {
    return tin.variant.title;
  }

  return tin.product?.title;
};

/**
 * Stand-in line items for the freebies a customer picks during checkout: the
 * tins, and whichever promo code option they chose. Both
 * are added to the order behind the scenes, so the cart never hands them back
 * as line items of its own and the summary would otherwise show nothing.
 */
export const getFreeDisplayLineItems = (cart) => {
  const items = [];

  // One coffee tin plus one matcha tin, so the first of each kind.
  const tinKindsShown = new Set();

  const addTin = (tin, kind) => {
    if (!tin || tinKindsShown.has(kind)) {
      return;
    }

    tinKindsShown.add(kind);
    items.push(
      freeDisplayLineItem({
        id: `free-tin-${tin.id}`,
        imageUrl: (tin.variant?.image || tin.product?.image)?.url,
        title: tinTitle(tin),
        type: "gwp",
      }),
    );
  };

  cart.products.forEach((product) => {
    addTin(
      product.children?.find(
        (child) => child.type === "rule" && child.removed === false,
      ),
      isMatchaLineItem(product) ? "matcha" : "coffee",
    );
  });

  // The summary hides the matcha tin's own line, so it shows here unless a
  // matcha product's tin choice above already stands for it.
  addTin(
    cart.products.find(
      (product) =>
        product.variant?.sku === FREE_MATCHA_TIN_SKU && !product.removed,
    ),
    "matcha",
  );

  // Keyed off the code the cart has actually accepted, not the one that was
  // clicked: a code that failed to apply shouldn't leave a product in the
  // summary the customer isn't getting.
  const appliedCode = cart.discount?.code;

  const promoItem = [promoOptionsData.itemA, promoOptionsData.itemB].find(
    (item) =>
      item.showItem &&
      appliedCode &&
      item.code.toUpperCase() === appliedCode.toUpperCase(),
  );

  if (promoOptionsData.showBlock && promoItem) {
    items.push(
      freeDisplayLineItem({
        id: `promo-${promoItem.code}`,
        imageUrl: promoItem.imageURL,
        title: promoItem.lineItemTitle,
        type: "discount",
      }),
    );
  }

  return items;
};
